import { useState, useCallback, useEffect, useRef } from "react";
import { enqueueSnackbar } from "notistack";
import { v4 as uuidv4 } from "uuid";
import { useNavigate } from "react-router-dom";
import { useApiClient } from "../../../api";
import { useAssistantStore } from "../../../stores/assistant";
import { useAuthenticationStore } from "../../../stores/auth";

export const useAssistantChat = (
  initialContext = {},
  initialConversationId = null,
  mode = "full",
  initialRequest = null,
) => {
  const apiClient = useApiClient();
  const navigate = useNavigate();
  const actor = useAuthenticationStore(
    (s) =>
      `${s.user.info?.id || ""}:${s.user.signInSession || ""}:${s.user.info?.activeContext?.roleAssignment?.id || ""}`,
  );
  const contextKey = JSON.stringify([
    initialContext.assignment_id,
    initialContext.class_id,
    initialContext.creation,
  ]);
  const [messages, setMessages] = useState(() =>
    mode === "embedded" ? useAssistantStore.getState().messages : [],
  );
  const [conversationId, setConversationId] = useState(
    () =>
      initialConversationId ||
      (mode === "embedded"
        ? useAssistantStore.getState().conversationId
        : null),
  );
  const [context, setContext] = useState(initialContext);
  const [isLoading, setIsLoading] = useState(false);
  const [retryAvailable, setRetryAvailable] = useState(false);
  const [assignmentName, setAssignmentName] = useState(
    initialContext.assignment_name || null,
  );
  const [creationOptions, setCreationOptions] = useState(null);
  const [creationOptionsError, setCreationOptionsError] = useState(false);
  const [className, setClassName] = useState(initialContext.class_name || null);
  const generation = useRef(0);
  const busy = useRef(false);
  const pending = useRef(null);
  const currentConversation = useRef(conversationId);
  const initialRoute = useRef(initialConversationId);
  const scopeRef = useRef(`${actor}:${contextKey}`);
  const actorRef = useRef(actor);
  actorRef.current = actor;

  useEffect(() => {
    // A new server ID belongs to the turn that just completed, not another chat.
    if (
      initialRoute.current !== initialConversationId &&
      initialConversationId !== currentConversation.current
    ) {
      generation.current += 1;
      busy.current = false;
      pending.current = null;
      setRetryAvailable(false);
      setIsLoading(false);
      setMessages([]);
      setConversationId(initialConversationId);
      currentConversation.current = initialConversationId;
    }
    initialRoute.current = initialConversationId;
  }, [initialConversationId]);

  useEffect(() => {
    if (scopeRef.current !== `${actor}:${contextKey}`) {
      scopeRef.current = `${actor}:${contextKey}`;
      pending.current = null;
      currentConversation.current = initialConversationId;
      setConversationId(initialConversationId);
      setMessages([]);
      setRetryAvailable(false);
      setIsLoading(false);
      setAssignmentName(initialContext.assignment_name || null);
      setClassName(initialContext.class_name || null);
    }
    setContext(initialContext);
    return () => {
      generation.current += 1;
      busy.current = false;
    };
    // Context identity consists of validated entity IDs, not the object reference.
  }, [contextKey, actor]);

  useEffect(() => {
    if (mode === "embedded") useAssistantStore.getState().setMessages(messages);
  }, [messages, mode]);

  useEffect(() => {
    let active = true;
    setCreationOptions(null);
    setCreationOptionsError(false);
    if (!context.creation || !context.class_id) return;
    const classId = context.class_id;
    apiClient.assistant
      .getCreationOptions(classId)
      .then((options) => {
        if (!active || !options) return;
        setCreationOptions(options);
        setClassName(options.className);
        if (
          mode === "embedded" &&
          useAssistantStore.getState().currentContext?.class_id === classId
        ) {
          useAssistantStore.setState({ className: options.className });
        }
      })
      .catch((error) => {
        if (active) {
          setCreationOptionsError(true);
          console.error("Unable to load Bud's creation options", error);
        }
      });
    return () => {
      active = false;
    };
  }, [
    context.class_id,
    context.class_name,
    context.creation?.kind,
    actor,
    mode,
  ]);

  const run = useCallback(
    async (payload, isPromptUsage, retry = false) => {
      if (busy.current) return;
      busy.current = true;
      setIsLoading(true);
      setRetryAvailable(false);
      const turn = generation.current;
      const startedActor = actor;
      const live = () =>
        turn === generation.current && actorRef.current === startedActor;
      pending.current = { payload, isPromptUsage };
      try {
        let response;
        // Following a lost response, inspect the durable result before asking to retry.
        if (retry)
          response = await apiClient.assistant.getRequest(payload.requestId);
        if (!live()) return;
        if (!response || response.requestStatus === "failed") {
          response = await apiClient.assistant.sendMessage({
            ...payload,
            retry,
          });
        }
        for (
          let poll = 0;
          response?.requestStatus === "running" && poll < 65 && live();
          poll += 1
        ) {
          await new Promise((resolve) => setTimeout(resolve, 2000));
          if (!live()) return;
          response = await apiClient.assistant.getRequest(payload.requestId);
        }
        if (!live()) return;
        if (!response || response.requestStatus === "running")
          throw new Error("Request still running");
        if (
          response.conversationId &&
          !response.error &&
          payload.context?.creation?.guided_quiz
        ) {
          const actions = await apiClient.assistant.getActions(
            response.conversationId,
          );
          if (!live()) return;
          const action = actions.find(
            (item) => item.requestId === payload.requestId,
          );
          if (!action) throw new Error("Quiz proposal could not be confirmed");
          if (action.status === "proposed") {
            let generated;
            try {
              generated = await apiClient.assistant.performAction(
                action.id,
                "generate",
                action.revision,
              );
            } catch (error) {
              if (!live()) return;
              console.error("Bud quiz generation confirmation failed", error);
              // A transport error does not mean the mutation was rolled back.
              // Read durable state before offering another generation attempt.
              const recovered = await apiClient.assistant.getActions(
                response.conversationId,
              );
              if (!live()) return;
              const saved = recovered.find(
                (item) => item.requestId === payload.requestId,
              );
              if (
                !saved ||
                ![
                  "queued",
                  "generating",
                  "ready",
                  "published",
                  "failed",
                  "cancelled",
                ].includes(saved.status)
              )
                throw error;
              generated = { success: true, action: saved };
            }
            if (!live()) return;
            if (!generated.success) {
              const message =
                generated.error ||
                "Quiz generation could not start. Retry from the draft card.";
              enqueueSnackbar(message, { variant: "error" });
              response = { ...response, message };
            }
          }
        }
        if (
          response.conversationId &&
          !response.error &&
          (payload.context?.creation?.questions ||
            payload.context?.creation?.guided_quiz)
        ) {
          setContext((current) => {
            const rest = { ...current };
            delete rest.guided_setup;
            return rest;
          });
          if (mode === "embedded") {
            const rest = { ...useAssistantStore.getState().currentContext };
            delete rest.guided_setup;
            useAssistantStore.setState({ currentContext: rest });
          }
          useAssistantStore.getState().setFullPageLaunch(null);
        }
        if (response.conversationId) {
          currentConversation.current = response.conversationId;
          setConversationId(response.conversationId);
          if (mode === "embedded")
            useAssistantStore
              .getState()
              .setConversationId(response.conversationId);
        }
        setMessages((prev) => [
          ...prev,
          {
            role: "assistant",
            content: response.error || response.message,
            isError: !!response.error,
            timestamp: new Date(),
          },
        ]);
        setRetryAvailable(response.requestStatus === "failed");
        if (response.assignmentName) setAssignmentName(response.assignmentName);
        if (response.className) setClassName(response.className);
        if (isPromptUsage && !response.error && response.conversationId) {
          // Analytics must not turn a successfully completed chat into a retry.
          apiClient.assistant
            .recordPromptUsage({
              promptText: payload.message,
              conversationId: response.conversationId,
              contextData: payload.context,
            })
            .catch(() => {});
        }
        if (
          mode === "full" &&
          !payload.conversationId &&
          response.conversationId &&
          !response.error
        ) {
          navigate(`/assistant?conversation=${response.conversationId}`, {
            replace: true,
          });
        }
      } catch (error) {
        if (!live()) return;
        console.error("Bud request confirmation failed", error);
        setMessages((prev) => [
          ...prev,
          {
            role: "assistant",
            content:
              "Bud's response could not be confirmed. Retry to check its status safely.",
            isError: true,
            timestamp: new Date(),
          },
        ]);
        setRetryAvailable(true);
      } finally {
        if (live()) {
          busy.current = false;
          setIsLoading(false);
        }
      }
    },
    [actor, apiClient.assistant, mode, navigate],
  );

  const sendMessage = useCallback(
    async (text, { isPromptUsage = false, creation } = {}) => {
      if (busy.current || !text.trim()) return;
      setMessages((prev) => [
        ...prev,
        { role: "user", content: text, timestamp: new Date() },
      ]);
      return run(
        {
          message: text,
          conversationId: currentConversation.current,
          context: creation ? { ...context, creation } : context,
          requestId: uuidv4(),
        },
        isPromptUsage,
      );
    },
    [context, run],
  );

  useEffect(() => {
    if (
      initialRequest &&
      ["running", "failed"].includes(initialRequest.requestStatus) &&
      !pending.current
    ) {
      pending.current = {
        payload: {
          message: initialRequest.originalMessage,
          conversationId: initialRequest.originalConversationId,
          context: {
            ...initialContext,
            ...(initialRequest.originalCreation
              ? { creation: initialRequest.originalCreation }
              : {}),
          },
          requestId: initialRequest.requestId,
        },
        isPromptUsage: false,
      };
      setRetryAvailable(true);
    }
    // Only restore a persisted request when the route's loaded request changes.
  }, [initialRequest?.requestId]);

  const retryMessage = useCallback(
    () =>
      pending.current &&
      run(pending.current.payload, pending.current.isPromptUsage, true),
    [run],
  );
  const selectClass = useCallback(
    (schoolClass, kind) => {
      if (busy.current || context.class_id || context.assignment_id)
        return false;
      const next = {
        ...context,
        class_id: schoolClass.id,
        class_name: schoolClass.name,
        creation: context.creation || { kind },
        ...(["assignment", "quiz"].includes(context.creation?.kind || kind)
          ? { guided_setup: null }
          : {}),
      };
      setContext(next);
      setClassName(schoolClass.name);
      pending.current = null;
      setRetryAvailable(false);
      if (mode === "embedded") {
        // This is a selection inside the same conversation, not a new launch.
        // Preserve existing messages when the shared panel receives the context.
        scopeRef.current = `${actor}:${JSON.stringify([next.assignment_id, next.class_id, next.creation])}`;
        useAssistantStore.setState({
          currentContext: next,
          className: schoolClass.name,
        });
      } else if (!currentConversation.current) {
        scopeRef.current = `${actor}:${JSON.stringify([next.assignment_id, next.class_id, next.creation])}`;
        useAssistantStore.getState().setFullPageLaunch(next);
      }
      return true;
    },
    [context, mode, actor],
  );
  const updateGuidedSetup = useCallback(
    (guided_setup) => {
      const next = { ...context, guided_setup };
      setContext(next);
      if (mode === "embedded")
        useAssistantStore.setState({ currentContext: next });
      useAssistantStore.getState().setFullPageLaunch(next);
    },
    [context, mode],
  );

  const clearMessages = useCallback(() => {
    generation.current += 1;
    busy.current = false;
    pending.current = null;
    currentConversation.current = null;
    setMessages([]);
    setConversationId(null);
    setIsLoading(false);
    setRetryAvailable(false);
  }, []);

  return {
    messages,
    setMessages,
    isLoading,
    conversationId,
    context,
    setContext,
    selectClass,
    updateGuidedSetup,
    sendMessage,
    retryMessage,
    retryAvailable,
    clearMessages,
    assignmentName,
    className,
    creationOptions,
    creationOptionsError,
  };
};
